import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  delay,
  finalize,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  BankAccount,
  Card,
  CardNetwork,
  Wallet,
  WalletTransaction,
} from './wallet.model';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { GlobalProgressSpinnerService } from '@shared/services/global-progress-spinner.service';
import { transitions } from '@shared/dummyData/transactions.data';
import { CartItem, CartPayload } from '../cart/cart.model';
import { COLLECTION, db } from '@shared/helpers/database';
import { functions } from '@shared/helpers/appwrite';
import { environment } from '../../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  readonly wallet$: Observable<Wallet>;
  readonly walletTransactions$: Observable<WalletTransaction[]>;
  readonly cards$: Observable<Card[]>;
  readonly bankAccounts$: Observable<BankAccount[]>;
  readonly selectedTransactionMethod$: Observable<Card | BankAccount | null>;

  readonly isWalletLoading$: Observable<boolean>;
  readonly isWalletTransactionsLoading$: Observable<boolean>;
  readonly isCardsLoading$: Observable<boolean>;
  readonly isBankAccountsLoading$: Observable<boolean>;

  private readonly _wallet$: BehaviorSubject<Wallet> =
    new BehaviorSubject<Wallet>({
      cashBalance: 0,
      rewardsBalance: 0,
      isCashBalanceWithdrawable: false,
    });
  private readonly _walletTransactions$: BehaviorSubject<WalletTransaction[]> =
    new BehaviorSubject<WalletTransaction[]>([]);
  private readonly _cards$: BehaviorSubject<Card[]> = new BehaviorSubject<
    Card[]
  >([]);
  private readonly _bankAccounts$: BehaviorSubject<BankAccount[]> =
    new BehaviorSubject<BankAccount[]>([]);
  private readonly _isWalletLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private readonly _isWalletTransactionsLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private readonly _isCardsLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private readonly _isBankAccountsLoading$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private readonly _selectedTransactionMethod$: Subject<
    Card | BankAccount | null
  > = new Subject<Card | BankAccount | null>();

  private readonly _globalProgressSpinnerService = inject(
    GlobalProgressSpinnerService
  );

  constructor() {
    this.wallet$ = this._wallet$.asObservable();
    this.walletTransactions$ = this._walletTransactions$.asObservable();
    this.cards$ = this._cards$.asObservable();
    this.bankAccounts$ = this._bankAccounts$.asObservable();
    this.selectedTransactionMethod$ =
      this._selectedTransactionMethod$.asObservable();

    this.isWalletLoading$ = this._isWalletLoading$.asObservable();
    this.isWalletTransactionsLoading$ =
      this._isWalletTransactionsLoading$.asObservable();
    this.isCardsLoading$ = this._isCardsLoading$.asObservable();
    this.isBankAccountsLoading$ = this._isBankAccountsLoading$.asObservable();

    // this.initializeWallet();
  }

  // Remove this latter just for demo
  initializeWallet(): void {
    this._isWalletLoading$.next(true);

    setTimeout(() => {
      const wallet: Wallet = {
        cashBalance: 1234,
        rewardsBalance: 42,
        isCashBalanceWithdrawable: true,
      };

      this._wallet$.next(wallet);

      this._isWalletLoading$.next(false);
    }, 200);
  }

  getWallet(): void {
    this._isWalletLoading$.next(true);

    db[COLLECTION.WALLET].list().then((wallets: any) => {
      // Each user has only one wallet
      const wallet: Wallet = wallets.documents[0];
      this._wallet$.next(wallet);
      this._isWalletLoading$.next(false);
    });
  }

  setWallet(amount: number): void {
    this._isWalletLoading$.next(true);

    setTimeout(() => {
      const currentWallet = this._wallet$.getValue();

      this._wallet$.next({
        ...currentWallet,
        cashBalance: currentWallet.cashBalance + amount,
      });

      // API CALL set the value of the wallet
      this._isWalletLoading$.next(false);
    }, 200);
  }

  getWalletTransactions(): void {
    this._isWalletTransactionsLoading$.next(true);

    db[COLLECTION.WALLET_TRANSACTIONS].list().then((res: any) => {
      this._walletTransactions$.next(res.documents);
      this._isWalletTransactionsLoading$.next(false);
    });

    // setTimeout(() => {
    //   const walletTransactions: WalletTransaction[] = [...transitions];

    //   this._walletTransactions$.next(walletTransactions);

    //   this._isWalletTransactionsLoading$.next(false);
    // }, 500);
  }

  getCards(): void {
    this._isCardsLoading$.next(true);

    setTimeout(() => {
      // Get the cards from the API
      // const cards: Card[] = [];
      // this._cards$.next(cards);

      this._isCardsLoading$.next(false);
    }, 200);
  }

  getBankAccounts(): void {
    this._isBankAccountsLoading$.next(true);

    setTimeout(() => {
      // Get the bank acccounts from the API
      // const bankAccounts: BankAccount[] = [];
      // this._bankAccounts$.next(bankAccounts);

      this._isBankAccountsLoading$.next(false);
    }, 200);
  }

  addNewCard(card: Card): void {
    // Call api, update this latter
    card.network = CardNetwork.VISA;
    card.cardNumber = '**** ' + card.cardNumber.slice(-4);
    this._cards$.next([...this._cards$.getValue(), card]);
  }

  addNewBankAccount(bankAccount: BankAccount): void {
    // Call api, update this later
    bankAccount.iban = '**** ' + bankAccount.iban.slice(-4);
    this._bankAccounts$.next([...this._bankAccounts$.getValue(), bankAccount]);
  }

  removeBankAccount(bankAccount: BankAccount): void {
    // Call api, update this later

    const bankAccounts: BankAccount[] = this._bankAccounts$
      .getValue()
      .filter((b) => b.iban !== bankAccount.iban);

    this._bankAccounts$.next([...bankAccounts]);
  }

  setTransactionMethod(method: Card | BankAccount): void {
    this._selectedTransactionMethod$.next(method);
  }

  onDeposit(amount: number): Observable<any> {
    this._globalProgressSpinnerService.showLoader();

    this.setWallet(+amount);

    return of(amount)
      .pipe(delay(1000))
      .pipe(
        finalize(() => {
          this._globalProgressSpinnerService.hideLoader();
        })
      );
  }

  onWithdraw(amount: number): Observable<any> {
    this._globalProgressSpinnerService.showLoader();

    this.setWallet(-amount);

    return of(amount)
      .pipe(delay(1000))
      .pipe(
        finalize(() => {
          this._globalProgressSpinnerService.hideLoader();
        })
      );
  }

  onInvest(cart: CartItem[]): Promise<any> {
    this._globalProgressSpinnerService.showLoader();

    const cartPaylod: CartPayload[] = cart.map((item) => ({
      propertyId: item.property.$id,
      amount: item.amount,
    }));
    return functions.createExecution(
      environment.appwrite.functionIdBuyCart,
      JSON.stringify(cartPaylod),
      false
    );
  }

  // Helper methods
  luhnValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value?.replace(/\s+/g, '');

    if (!value || !/^\d+$/.test(value)) {
      return { invalidCardNumber: true };
    }

    let sum = 0;
    let shouldDouble = false;

    for (let i = value.length - 1; i >= 0; i--) {
      let digit = parseInt(value.charAt(i), 10);

      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      shouldDouble = !shouldDouble;
    }

    const isValid = sum % 10 === 0;
    return isValid ? null : { invalidCardNumber: true };
  }
}
